/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/** Provides the standard icon and theme-aware color for each supply type. */

import {
    Bath,
    Flame,
    Heater,
    Moon,
    PlugZap,
    SolarPanel,
    Sprout,
    type LucideIcon,
    type LucideProps,
} from 'lucide-react';
import { forwardRef } from 'react';

export const SUPPLY_ICON_TYPES = [
    'electricity',
    'gas',
    'heat',
    'photovoltaics',
    'report',
    'water',
] as const;

export type SupplyIconType = typeof SUPPLY_ICON_TYPES[number];

interface SupplyIconDefinition {
    color: string;
    icon: LucideIcon;
}

const SUPPLY_ICON_DEFINITIONS: Record<SupplyIconType, SupplyIconDefinition> = {
    electricity: {
        color: 'color-mix(in srgb, var(--color-success) 80%, var(--color-warning))',
        icon: PlugZap,
    },
    gas: {
        color: 'color-mix(in srgb, var(--color-warning) 75%, var(--color-error))',
        icon: Flame,
    },
    heat: {
        color: 'color-mix(in srgb, var(--color-error) 80%, var(--color-warning))',
        icon: Heater,
    },
    photovoltaics: {
        color: 'var(--color-primary)',
        icon: SolarPanel,
    },
    report: {
        color: 'var(--color-accent)',
        icon: Sprout,
    },
    water: {
        color: 'var(--color-info)',
        icon: Bath,
    },
};

export interface SupplyIconProps extends LucideProps {
    type: SupplyIconType;
}

export const SupplyIcon = forwardRef<SVGSVGElement, SupplyIconProps>(function SupplyIcon(
    { style, type, ...props },
    ref,
) {
    const { color, icon: Icon } = SUPPLY_ICON_DEFINITIONS[type];

    return (
        <Icon
            ref={ref}
            {...props}
            style={{ color, ...style }}
        />
    );
});

function createSupplyIcon(type: SupplyIconType): LucideIcon {
    return forwardRef<SVGSVGElement, LucideProps>(function SupplyTypeIcon(props, ref) {
        return <SupplyIcon ref={ref} {...props} type={type} />;
    });
}

export const ElectricityIcon = createSupplyIcon('electricity');
export const ElectricityNightIcon = forwardRef<SVGSVGElement, LucideProps>(function ElectricityNightIcon(
    { style, ...props },
    ref,
) {
    return (
        <svg
            ref={ref}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            {...props}
            style={{
                color: SUPPLY_ICON_DEFINITIONS.electricity.color,
                ...style,
            }}
        >
            <PlugZap className="h-full w-full" aria-hidden="true" />
            <Moon
                x="0"
                y="0"
                width="9"
                height="9"
                strokeWidth="2.5"
                style={{ color: 'var(--color-primary)' }}
                aria-hidden="true"
            />
        </svg>
    );
});
export const GasIcon = createSupplyIcon('gas');
export const HeatIcon = createSupplyIcon('heat');
export const PhotovoltaicsIcon = createSupplyIcon('photovoltaics');
export const ReportIcon = createSupplyIcon('report');
export const WaterIcon = createSupplyIcon('water');
